import React, { useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

export interface CopyableIdProps {
  value?: string | null | undefined;
  label?: string | undefined;
  maxStart?: number | undefined;
  maxEnd?: number | undefined;
  variant?: 'caption' | 'body2' | undefined;
}

function abbreviateId(value: string, maxStart: number, maxEnd: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxStart + maxEnd + 3) return trimmed;
  return `${trimmed.slice(0, maxStart)}…${trimmed.slice(-maxEnd)}`;
}

export const CopyableId: React.FC<CopyableIdProps> = ({
  value,
  label = 'ID',
  maxStart = 8,
  maxEnd = 4,
  variant = 'caption'
}) => {
  const [copied, setCopied] = useState(false);
  const trimmed = value?.trim();
  if (!trimmed) return <Typography variant={variant}>—</Typography>;

  const handleCopy = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? 'Copiado' : `${label}: ${trimmed} — clique para copiar`}>
      <Typography
        component="button"
        type="button"
        variant={variant}
        onClick={handleCopy}
        sx={{
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: copied ? 'success.main' : 'text.secondary',
          cursor: 'copy',
          fontFamily: 'monospace',
          textAlign: 'left',
          '&:hover': { color: 'primary.main', textDecoration: 'underline' }
        }}
      >
        {abbreviateId(trimmed, maxStart, maxEnd)}
      </Typography>
    </Tooltip>
  );
};

export default CopyableId;
